import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs/Rx';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/toPromise';
import { AppConfig } from '../../app.config';
import { Access, UserAccess } from '../models/userModel';
import { StorageConstants, API, ErrorTypes } from '../utility/constants';
import { LookUps } from '../models/lookUps';
import { UploadResult } from '../models/uploadResult';
import { AppInsightService } from './app-insight.service';

@Injectable()
export class DataService {
    private lookUps: LookUps;
    constructor(private http: HttpClient, private config: AppConfig, protected appInsights: AppInsightService) {}

    private get<T>(action: string, fromObject: any = null): Observable<any> {
        return this.http
            .get(this.config.getConfig('rootUrl') + action, { params: new HttpParams({fromObject}) })
            .map(this.checkResults)
            .catch(this.handleErrorObservable);
    }

    public post<T>(action: string, body: any, isToken = true): Observable<T> {
        return this.http
            .post(this.config.getConfig('rootUrl') + action, body)
            .map(res => res as T)
            .catch(this.handleErrorObservable);
    }

    private checkResults(res: any) {
        if (!res || res === '') {
            return true;
        } else {
            return res;
        }
    }

    private getParams(obj): HttpParams {
     return  new HttpParams({fromObject: obj});
    }

    public httpget<T>(action: string,  params: any = null ): Promise<T> {
        return this.http
            .get(this.config.getConfig('rootUrl') + action , { params: this.getParams(params) })
            .toPromise()
            .then(res => res as T)
            .catch(this.handleErrorPromise);
    }

    private httpgetblob<T>(action: string): Promise<any> {
        return this.http
            .get(this.config.getConfig('rootUrl') + action, { responseType: 'blob'} )
            .toPromise()
            .then(res => res)
            .catch(err => alert('Something went wrong. Error message: ' + err));
    }

    public httppost<T>(action: string, body: any, options: any = null): Promise<T> {
        return this.http
            .post(this.config.getConfig('rootUrl') + action, body)
            .toPromise()
            .then(res => res as T)
            .catch(this.handleErrorPromise);
    }

    public httppostwithoptions(action: string, body: any, options = null): Promise<ArrayBuffer> {
        return this.http
            .post(this.config.getConfig('rootUrl') + action, body, options = options)
            .toPromise()
            .catch(this.handleErrorPromise);
    }

    public getAccessRefresh(): Observable<Access> {
        return  this.get<Access>('/user/groups')
        .map(response => {
            let access =  <Access>response;
            sessionStorage.setItem(StorageConstants.Access, JSON.stringify(access));
             return access;
        });
    }

    public clearAccess(): void {
        sessionStorage.removeItem(StorageConstants.Access);
    }

    public getAccess(): Observable<Access> {
       let access =  sessionStorage.getItem(StorageConstants.Access);
       if (access) { return Observable.create( (observer) =>  observer.next( JSON.parse(access))); }
       return this.getAccessRefresh();
    }

    public setUserAccess(userAccess: UserAccess): Observable<UserAccess> {
        return this.post(API.SetUserAccess, userAccess);
    }

    public getTemplate(id: string =''): Promise<any> {
        return this.httpget<any>(`/template/${id}`);
    }
    
    public getUsers(): Promise<UserAccess[]> {
            return this.httpget(`/user/users`);
    }

    public getLookUps(): Observable<LookUps> {
            if ( this.lookUps ) {
              return Observable.create( (observer) =>  observer.next(this.lookUps));
            }
            return this.get(API.LookUps).map( r =>  {return  <LookUps>r; });
    }

    public uploadFiles(files: Array<any>, isNonFatalUpload:boolean = false):  Observable<UploadResult> {
        let headers = new Headers();
            let formData: FormData = new FormData();
            for (let file of files) {
                formData.append('files', file, file.name);
            }

            headers.set('Accept', 'text/csv');
            return  this.post<UploadResult>(`/ocme/upload/web/` + isNonFatalUpload, formData);
    }

    public uploadDocuments(file: any, path:string,jurisdiction: string = null): Promise<string> {
        let headers = new Headers();
        headers.set('Accept', 'text/pdf');
        let formData: FormData = new FormData();
            formData.append('file', file, file.name);
            formData.append('path', path);

        return this.httppost<any>(`/resource/upload?jurisdiction=${jurisdiction}`, formData);
    }

    public uploadAnnualReportFile(files: Array<any>, year:string, jurisdiction:string, reportType:string): Promise<string> {
        let headers = new Headers();
        if (files.length > 0) {
            let formData: FormData = new FormData();
            for (let file of files) {
                formData.append('files', file, file.name);
            }

            headers.set('Accept', 'text/pdf');
            var url = '/annualreport/upload/'
            url += (year + '/');
            url += (jurisdiction + '/');
            url += (reportType);
            return this.httppost<string>(url, formData);
        }
    }
s
    public ping(): Observable<string> {
        return this.get<string>('/Account/ping');
    }

    //pdmp and alerts use GetCaseById. Must match api and take the case id instead.
    public getPDMPData(caseId: string): Promise<any> {
        return this.httpget<string>(`/pdmp/${caseId}`);
    }
    
    public getAlertsData(caseId: string, lookUpDuration: any): Promise<any> {
        return this.httpget<string>(`/alerts/${caseId}?lookUpDuration=${lookUpDuration}`);
    }
    
    private handleErrorPromise = (error: any = null) => {
        if (!error) return Promise.reject('unknown error');

        var message = this.handleError(error) ;
        
        return Promise.reject(message || error.message || error);
    }
    
    private handleErrorObservable = (error: any = null) => {
      if (!error) return Promise.reject('unknown error');

      this.handleError(error) ;

      return Observable.throw(error.message || error) ;
    }

    private handleError = (error) => {
      if(error && error.status == 401) {
        console.log('Access token expired or unauthorized!');
        if(error.url.includes(ErrorTypes.CreateTokenError)) {
            throw error;
        } else {
            return;
        }
      }
      if (error && error.status == 413) {
        console.error(error.error);
        this.appInsights.logError({
            'error': error,
            'message': error.error || 'File too large to upload.'
        });
        return 'File too large to upload. Please try a smaller file.';
      }
      if(error && error.error && error.error.type === ErrorTypes.ResetPasswordError) {
        throw error;
      }
      if (error && error.error) {
        //Handle generic 400 and 500 responses with simple string message
        let message = error.error;

        //No idea what response this would be, but keep it
        if (error.error.Message ) { message += error.error.Message; }

        //Handled 400 responses thats return a modelstate
        if(error.error.ModelState ){ 
            for ( let propertyName in  error.error.ModelState) {
                message += error.error.ModelState[propertyName];
              }
        }
          
        console.error(message);
        this.appInsights.logError({
            'error': error,
            'message': message
        });
      } else {
        console.error(error);
        this.appInsights.logError(error);
        alert('An error occurred in the service call. Please try again.  If the issue continues, please contact support.');
      }
    }
}

